import type { MathWorksheetProblemV2, ProceduralHintRow } from '../learningOrchestrator/worksheetTypes';

type AnswerMode = 'study' | 'correction' | 'locked';

type Props = {
  problem: MathWorksheetProblemV2;
  answerValue?: string;
  answerMode?: AnswerMode;
  correctionRequired?: boolean;
  onAnswerChange?: (value: string) => void;
  onAnswerInteraction?: () => void;
};

function HintRow({ row }: { row: ProceduralHintRow }) {
  const totalColumns = 4;
  const cells = Array.from({ length: totalColumns }, (_, column) => {
    const start = totalColumns - row.placeValueOffset - row.digitCount;
    const active = column >= start && column < start + row.digitCount;
    return <span key={column} className={active ? 'v2-procedural-slot' : 'v2-procedural-empty'} />;
  });
  return (
    <div className="v2-procedural-row">
      <span className="v2-procedural-sign">{row.showLeadingOperator ? '+' : ''}</span>
      {cells}
    </div>
  );
}

export function MathWorksheetProblemBlock({
  problem,
  answerValue = '',
  answerMode = 'study',
  correctionRequired = false,
  onAnswerChange,
  onAnswerInteraction,
}: Props) {
  const inputId = `v2-answer-${problem.problemId.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  const locked = answerMode === 'locked';
  const support = problem.sequenceMeta.surfaceSupport.toLowerCase();
  const promptLength = `${problem.displayPrompt ?? ''} ${problem.supportingText ?? ''}`.trim().length;
  const promptDensity = promptLength > 92 ? 'xlong' : promptLength > 58 ? 'long' : 'normal';
  return (
    <section
      className={`v2-problem-block${correctionRequired ? ' v2-problem-block--correction' : ''}`}
      data-problem-id={problem.problemId}
      data-question-instance-id={problem.submissionBinding?.questionInstanceId}
      style={{
        left: `${problem.placement.x * 100}%`,
        top: `${problem.placement.y * 100}%`,
        width: `${problem.placement.width * 100}%`,
        height: `${problem.placement.height * 100}%`,
      }}
    >
      <div className="v2-problem-sequence" aria-label={`Soal ${problem.sequenceMeta.ordinal} dari ${problem.sequenceMeta.total}`}>
        <span>{problem.sequenceMeta.ordinal}</span>
        <small>{problem.sequenceMeta.phase === 'SUPPORTED' ? 'mulai' : problem.sequenceMeta.phase === 'FADING' ? 'kurangi bantuan' : 'mandiri'}</small>
      </div>
      {problem.operation === 'multiplication' ? (
        <div className="v2-problem-expression">
          <div>{problem.multiplicand}</div>
          <div><span>×</span><span>{problem.multiplier}</span></div>
          <div className="v2-problem-rule" />
        </div>
      ) : (
        <div className={`v2-generic-prompt v2-generic-prompt--${promptDensity}`}>
          <strong>{problem.displayPrompt ?? 'Soal BelajarKu'}</strong>
          {problem.supportingText && <span>{problem.supportingText}</span>}
        </div>
      )}
      {problem.operation === 'multiplication' && problem.scaffold !== 'S0' && problem.workTemplate ? (
        <div className={`v2-work-template v2-scaffold-${problem.scaffold.toLowerCase()}`} data-ink-region="work">
          {problem.workTemplate.rows.map((row) => <HintRow key={row.rowId} row={row} />)}
        </div>
      ) : (
        <div className={`v2-independent-work-area v2-work-area--${support}`} data-ink-region="work">
          {problem.sequenceMeta.surfaceSupport === 'FULL' && <span className="v2-work-area-label">Area kerja</span>}
          {problem.sequenceMeta.surfaceSupport !== 'MINIMAL' && <span className="v2-work-area-guide" aria-hidden="true" />}
        </div>
      )}
      <div className="v2-final-answer-capture">
        <label htmlFor={inputId}>{answerMode === 'correction' ? 'Perbaiki hasil akhir' : 'Hasil akhir'}</label>
        <input
          id={inputId}
          aria-label={`Jawaban akhir soal ${problem.problemId}`}
          className="v2-final-answer-input"
          inputMode="numeric"
          autoComplete="off"
          value={answerValue}
          disabled={locked || !onAnswerChange}
          onFocus={onAnswerInteraction}
          onPointerDown={onAnswerInteraction}
          onChange={(event) => onAnswerChange?.(event.target.value)}
        />
        {correctionRequired && (
          <span className="v2-correction-message" role="status">Belum tepat. Coba lagi tanpa melihat jawaban.</span>
        )}
      </div>
    </section>
  );
}
